import { z } from 'zod';

/** Bounded structural evidence, never document text or signed storage URLs. */
export const DocxIntegrityIssueSchema = z.object({
  code: z.string().max(80),
  part: z.string().max(240).optional(),
  location: z.string().max(120).optional(),
  action: z.string().max(600),
});
export type DocxIntegrityIssue = z.infer<typeof DocxIntegrityIssueSchema>;

export const DocxIntegrityRepairSchema = z.object({
  code: z.string().max(80),
  part: z.string().max(240),
  count: z.number().int().positive(),
});
export type DocxIntegrityRepair = z.infer<typeof DocxIntegrityRepairSchema>;

/**
 * Issue code of a Canvas save whose structural check did NOT COMPLETE (#1743):
 * `prepareDocx`, run as an isolated child computation, threw or exceeded its time
 * or memory bound. It is not a verdict on the document, hence deliberately NOT prefixed
 * `DOCX_`: that prefix means « this document is defective, correct its format »,
 * and the legalworkspace keys its « correction de format » message on it.
 * The previous validated version is untouched and the same snapshot may simply
 * be retried.
 */
export const CANVAS_CANDIDATE_VERIFICATION_UNAVAILABLE = 'CANDIDATE_VERIFICATION_UNAVAILABLE';

/**
 * Issue code of a Canvas save, filing or download that names an attempt another
 * page of the same conversation has since replaced. Not a verdict on the
 * document: the legalworkspace says why and offers to show the latest version.
 */
export const CANVAS_ATTEMPT_SUPERSEDED = 'ATTEMPT_SUPERSEDED';

/**
 * True for a size ceiling of `@hector/docx-integrity` (`DOCX_XML_LIMIT`,
 * `DOCX_ARCHIVE_LIMIT`, …): the document is too large to be checked, not
 * malformed. Every consumer words it « trop volumineux », never « correction de
 * format » nor « pas un document Word valide » — re-saving cannot shrink it.
 *
 * @param code - A `DocxIntegrityIssue.code`
 * @returns Whether the code is a `DOCX_*_LIMIT` size ceiling
 */
export function isDocxSizeLimitCode(code: string): boolean {
  return code.startsWith('DOCX_') && code.endsWith('_LIMIT');
}
